<template>
  <div style="padding: 1vw">
    <div class="pageTitle">
      <h3>커넥터 관리</h3>
      <span class="pageDesc">EMR 커넥터가 설치된 병원 현황을 관리합니다.</span>
    </div>

    <!-- 통계 카드 -->
    <a-row :gutter="16" style="margin-bottom: 20px;">
      <a-col :span="5">
        <a-card>
          <a-statistic
            title="전체 커넥터"
            :value="stats.total"
            suffix="개"
          />
        </a-card>
      </a-col>
      <a-col :span="5">
        <a-card>
          <a-statistic
            title="최근 24시간 접속"
            :value="stats.recentLoginCount"
            :value-style="{ color: '#52c41a' }"
            suffix="개"
          />
        </a-card>
      </a-col>
      <a-col :span="5">
        <a-card>
          <a-statistic
            title="7일 이상 미접속"
            :value="stats.inactiveCount"
            :value-style="{ color: '#f5222d' }"
            suffix="개"
          />
        </a-card>
      </a-col>
      <a-col :span="5">
        <a-card>
          <a-statistic
            :title="'최신 버전' + (stats.latestVersion ? ' (' + stats.latestVersion + ')' : '')"
            :value="stats.latestVersionCount"
            :value-style="{ color: '#52c41a' }"
            suffix="개"
          />
        </a-card>
      </a-col>
      <a-col :span="4">
        <a-card>
          <a-statistic
            title="업데이트 필요"
            :value="stats.outdatedCount"
            :value-style="{ color: '#fa8c16' }"
            suffix="개"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- 검색 -->
    <div style="margin-bottom: 16px;">
      <a-input-search
        v-model:value="searchText"
        placeholder="병원명 또는 HW ID 검색"
        style="width: 320px;"
        allow-clear
        @search="handleSearch"
        @pressEnter="handleSearch"
      />
    </div>

    <!-- 테이블 -->
    <a-table
      class="ant-table-striped"
      :columns="columns"
      :data-source="items"
      :pagination="pagination"
      :loading="loading"
      @change="handleTableChange"
      row-key="hwId"
      size="small"
    />

    <!-- 공지사항 관리 -->
    <div style="margin-top: 24px;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
        <h3 style="font-weight: 700; margin: 0;">공지사항 관리</h3>
        <a-button type="primary" class="yellow-button" size="small" @click="showCreateModal">공지 작성</a-button>
      </div>
      <a-table
        class="ant-table-striped"
        :columns="noticeColumns"
        :data-source="notices"
        :pagination="noticePagination"
        :loading="noticeLoading"
        @change="handleNoticeTableChange"
        row-key="_id"
        size="small"
      />
    </div>

    <!-- 공지 작성/수정 모달 -->
    <a-modal
      :open="noticeModalVisible"
      :confirm-loading="noticeSubmitting"
      @cancel="closeNoticeModal"
      :footer="null"
      width="600px"
    >
      <template #title>
        <div class="modal-title">{{ isEditNotice ? '공지 수정' : '공지 작성' }}</div>
      </template>
      <div class="modal-content">
        <a-form layout="vertical">
          <a-form-item label="유형">
            <a-select v-model:value="noticeForm.type" style="width: 100%;">
              <a-select-option value="info">
                <a-tag color="blue">안내</a-tag> 커넥터 사용자에게 일반 안내 공지
              </a-select-option>
              <a-select-option value="forceUpdate">
                <a-tag color="orange">강제 업데이트</a-tag> 커넥터 강제 업데이트 트리거
              </a-select-option>
              <a-select-option value="urgent">
                <a-tag color="red">긴급</a-tag> 창 강제 활성화 + 모달 표시
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="제목" required>
            <a-input v-model:value="noticeForm.title" placeholder="공지 제목을 입력하세요" :maxlength="100" show-count />
          </a-form-item>
          <a-form-item label="내용">
            <a-textarea
              v-model:value="noticeForm.content"
              :rows="6"
              placeholder="공지 내용을 입력하세요"
              :maxlength="1000"
              show-count
            />
            <p v-if="noticeForm.type === 'forceUpdate'" style="color: #fa8c16; font-size: 12px; margin-top: 6px; margin-bottom: 0;">
              강제 업데이트 타입은 내용 없이 제목만으로도 발송 가능합니다.
            </p>
          </a-form-item>
          <a-form-item label="활성화">
            <a-switch v-model:checked="noticeForm.isActive" />
            <span style="margin-left: 8px; color: #888; font-size: 12px;">
              {{ noticeForm.isActive ? '즉시 발송됩니다' : '저장만 하고 발송하지 않습니다' }}
            </span>
          </a-form-item>
        </a-form>
        <div class="modal-footer">
          <a-button @click="closeNoticeModal">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="noticeSubmitting" @click="handleNoticeSubmit">
            {{ isEditNotice ? '수정' : '작성' }}
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 공지 삭제 확인 모달 -->
    <a-modal
      :open="deleteModalVisible"
      :confirm-loading="deleteLoading"
      @cancel="closeDeleteModal"
      :footer="null"
      width="440px"
    >
      <template #title>
        <div class="modal-title">공지 삭제</div>
      </template>
      <div class="modal-content">
        <div style="text-align: center; padding: 16px 0;">
          <div style="font-size: 40px; color: #ff4d4f; margin-bottom: 16px;">!</div>
          <p style="font-size: 15px; font-weight: 600; color: #333; margin-bottom: 8px;">
            "{{ deleteTarget?.title }}"
          </p>
          <p style="color: #888; font-size: 13px; margin-bottom: 0;">
            공지를 삭제하시겠습니까?<br/>삭제 시 연결된 커넥터에서도 즉시 제거됩니다.
          </p>
        </div>
        <div class="modal-footer">
          <a-button @click="closeDeleteModal">취소</a-button>
          <a-button type="primary" danger :loading="deleteLoading" @click="confirmDeleteNotice">삭제</a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import {
  getConnectorDashboard,
  getConnectorNotices,
  createConnectorNotice,
  updateConnectorNotice,
  deleteConnectorNotice,
} from './api';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ko';

dayjs.extend(relativeTime);
dayjs.locale('ko');

export default {
  name: 'ConnectorManage',
  data() {
    return {
      // 대시보드
      items: [],
      loading: false,
      searchText: '',
      stats: {
        total: 0,
        recentLoginCount: 0,
        inactiveCount: 0,
        latestVersion: null,
        latestVersionCount: 0,
        outdatedCount: 0,
      },
      pagination: {
        current: 1,
        pageSize: 50,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['20', '50', '100'],
      },
      columns: [
        {
          title: '병원명',
          dataIndex: 'companyName',
          ellipsis: true,
        },
        {
          title: '버전',
          dataIndex: 'connectorVersion',
          width: 100,
          align: 'center',
          customRender: ({ text }) => {
            return text ? <a-tag color="blue">{text}</a-tag> : '-';
          },
        },
        {
          title: 'OS',
          dataIndex: 'connectorOs',
          width: 80,
          align: 'center',
        },
        {
          title: 'HW ID',
          dataIndex: 'hwId',
          width: 140,
          ellipsis: true,
        },
        {
          title: '허용 IP',
          dataIndex: 'allowIps',
          width: 140,
          ellipsis: true,
          customRender: ({ text }) => {
            if (!text || (Array.isArray(text) && text.length === 0)) return '-';
            return Array.isArray(text) ? text.join(', ') : text;
          },
        },
        {
          title: '마지막 로그인',
          dataIndex: 'lastLoginAt',
          width: 150,
          customRender: ({ text }) => {
            if (!text) return '-';
            const now = dayjs();
            const loginTime = dayjs(text);
            const diffDays = now.diff(loginTime, 'day');
            let color = 'green';
            if (diffDays >= 7) {
              color = 'red';
            } else if (diffDays >= 1) {
              color = 'orange';
            }
            return <a-tag color={color}>{loginTime.fromNow()}</a-tag>;
          },
        },
        {
          title: '마지막 IP',
          dataIndex: 'lastLoginIp',
          width: 130,
        },
        {
          title: '토큰 발급일',
          dataIndex: 'tokenCreatedAt',
          width: 120,
          customRender: ({ text }) => {
            return text ? dayjs(text).format('YYYY-MM-DD') : '-';
          },
        },
      ],
      // 공지사항
      notices: [],
      noticeLoading: false,
      noticeSubmitting: false,
      noticeModalVisible: false,
      deleteModalVisible: false,
      deleteLoading: false,
      deleteTarget: null,
      isEditNotice: false,
      editNoticeId: null,
      noticeForm: {
        title: '',
        content: '',
        type: 'info',
        isActive: true,
      },
      noticePagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
      noticeColumns: [
        {
          title: '유형',
          dataIndex: 'type',
          width: 100,
          align: 'center',
          customRender: ({ text }) => {
            const map = { info: { color: 'blue', label: '안내' }, forceUpdate: { color: 'orange', label: '강제 업데이트' }, urgent: { color: 'red', label: '긴급' } };
            const item = map[text] || { color: 'default', label: text };
            return <a-tag color={item.color}>{item.label}</a-tag>;
          },
        },
        { title: '제목', dataIndex: 'title', ellipsis: true },
        {
          title: '상태',
          dataIndex: 'isActive',
          width: 80,
          align: 'center',
          customRender: ({ text }) => {
            return text ? <a-tag color="green">활성</a-tag> : <a-tag>비활성</a-tag>;
          },
        },
        {
          title: '작성자',
          dataIndex: 'createdByName',
          width: 100,
          align: 'center',
        },
        {
          title: '작성일',
          dataIndex: 'createdAt',
          width: 120,
          customRender: ({ text }) => text ? dayjs(text).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
          title: '관리',
          key: 'action',
          width: 150,
          align: 'center',
          customRender: ({ record }) => {
            return (
              <div style="display: flex; gap: 8px; justify-content: center;">
                <div class="certi-custom-btn" onClick={() => this.showEditModal(record)}>수정</div>
                <div class="certi-custom-btn" style="color: #cf1322;" onClick={() => this.handleDeleteNotice(record)}>삭제</div>
              </div>
            );
          },
        },
      ],
    };
  },
  async mounted() {
    await Promise.all([this.fetchDashboard(), this.fetchNotices()]);
  },
  methods: {
    // 대시보드
    async fetchDashboard() {
      this.loading = true;
      try {
        const result = await getConnectorDashboard(
          this.pagination.current,
          this.pagination.pageSize,
          this.searchText,
        );
        this.items = result.items;
        this.pagination.total = result.total;
        if (result.stats) {
          this.stats.total = result.total;
          this.stats.recentLoginCount = result.stats.recentLoginCount || 0;
          this.stats.inactiveCount = result.stats.inactiveCount || 0;
          this.stats.latestVersion = result.stats.latestVersion || null;
          this.stats.latestVersionCount = result.stats.latestVersionCount || 0;
          this.stats.outdatedCount = result.stats.outdatedCount || 0;
        }
      } catch (error) {
        console.error('커넥터 대시보드 조회 실패:', error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.pagination.current = 1;
      this.fetchDashboard();
    },
    handleTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.fetchDashboard();
    },
    // 공지사항
    async fetchNotices() {
      this.noticeLoading = true;
      try {
        const result = await getConnectorNotices(this.noticePagination.current, this.noticePagination.pageSize);
        this.notices = result.items;
        this.noticePagination.total = result.total;
      } catch (error) {
        console.error('공지 조회 실패:', error);
      } finally {
        this.noticeLoading = false;
      }
    },
    showCreateModal() {
      this.isEditNotice = false;
      this.editNoticeId = null;
      this.noticeForm = { title: '', content: '', type: 'info', isActive: true };
      this.noticeModalVisible = true;
    },
    showEditModal(record) {
      this.isEditNotice = true;
      this.editNoticeId = record._id;
      this.noticeForm = {
        title: record.title,
        content: record.content,
        type: record.type,
        isActive: record.isActive,
      };
      this.noticeModalVisible = true;
    },
    closeNoticeModal() {
      this.noticeModalVisible = false;
    },
    async handleNoticeSubmit() {
      if (!this.noticeForm.title.trim()) {
        message.warning('제목을 입력해주세요.');
        return;
      }
      if (this.noticeForm.type !== 'forceUpdate' && !this.noticeForm.content.trim()) {
        message.warning('내용을 입력해주세요.');
        return;
      }
      this.noticeSubmitting = true;
      try {
        if (this.isEditNotice) {
          await updateConnectorNotice(this.editNoticeId, this.noticeForm);
          message.success('공지가 수정되었습니다.');
        } else {
          await createConnectorNotice(this.noticeForm);
          message.success('공지가 작성되었습니다.');
        }
        this.closeNoticeModal();
        await this.fetchNotices();
      } catch (error) {
        message.error('처리 중 오류가 발생했습니다.');
      } finally {
        this.noticeSubmitting = false;
      }
    },
    handleDeleteNotice(record) {
      this.deleteTarget = record;
      this.deleteModalVisible = true;
    },
    closeDeleteModal() {
      this.deleteModalVisible = false;
      this.deleteTarget = null;
    },
    async confirmDeleteNotice() {
      if (!this.deleteTarget) return;
      this.deleteLoading = true;
      try {
        await deleteConnectorNotice(this.deleteTarget._id);
        message.success('공지가 삭제되었습니다.');
        this.closeDeleteModal();
        await this.fetchNotices();
      } catch (error) {
        message.error('삭제 중 오류가 발생했습니다.');
      } finally {
        this.deleteLoading = false;
      }
    },
    handleNoticeTableChange(pag) {
      this.noticePagination.current = pag.current;
      this.fetchNotices();
    },
  },
};
</script>

<style scoped>
.pageTitle {
  margin-bottom: 20px;
}
.pageTitle h3 {
  font-weight: 700;
  margin-bottom: 4px;
}
.pageDesc {
  color: #888;
  font-size: 13px;
}
</style>
